import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllBuffets,
  getSingleBuffet,
  createBuffet,
  updateBuffet,
  deleteBuffet,
} from "@/api/new-api/staff-management/menu-manage/special-menu/buffets";

// Thunks

export const fetchAllBuffets = createAsyncThunk(
  "buffets/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllBuffets(restaurant_id);
    if (res.status === "success") return res.data.buffets;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleBuffet = createAsyncThunk(
  "buffets/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleBuffet(id);
    if (res.status === "success") return res.data.buffet;
    return rejectWithValue(res.message);
  }
);

export const createNewBuffet = createAsyncThunk(
  "buffets/create",
  async (data, { rejectWithValue }) => {
    const res = await createBuffet(data);
    if (res.status === "success") return res.data.buffet;
    return rejectWithValue(res.message);
  }
);

export const updateExistingBuffet = createAsyncThunk(
  "buffets/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateBuffet(id, data);
    if (res.status === "success") return res.data.buffet;
    return rejectWithValue(res.message);
  }
);

export const deleteBuffetById = createAsyncThunk(
  "buffets/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteBuffet(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  buffets: [],
  singleBuffet: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const buffetSlice = createSlice({
  name: "buffets",
  initialState,
  reducers: {
    clearBuffetMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllBuffets.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllBuffets.fulfilled, (state, action) => {
        state.getLoading = false;
        state.buffets = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllBuffets.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleBuffet.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleBuffet.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleBuffet = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleBuffet.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewBuffet.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewBuffet.fulfilled, (state, action) => {
        state.createLoading = false;
        state.buffets.push(action.payload);
        state.success = "Buffet created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewBuffet.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingBuffet.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingBuffet.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.buffets = state.buffets.map((buffet) =>
          buffet.id === action.payload.id ? action.payload : buffet
        );
        state.success = "Buffet updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingBuffet.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteBuffetById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteBuffetById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.buffets = state.buffets.filter(
          (buffet) => buffet.id !== action.payload
        );
        state.success = "Buffet deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteBuffetById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearBuffetMessages } = buffetSlice.actions;
export default buffetSlice.reducer;
